import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import ByTheNumbersSection from './ByTheNumbersSection';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';

interface Project {
  _id: string;
  title: string;
  slug: string;
  description: string;
  imageUrl: string;
  category: string;
  network: string;
  tags: string[];
  featured: boolean;
}

interface TeamMember {
  _id: string;
  name: string;
  position: string;
  description: string;
  imageUrl: string;
  socialLinks: {
    twitter?: string;
    linkedin?: string;
    github?: string;
  };
  featured: boolean;
  createdAt?: string;
  displayOrder?: number;
}

interface ContactItem {
  _id: string;
  type: string;
  title: string;
  description: string;
  contact: string;
  icon: string;
  imageUrl: string;
  featured: boolean;
  displayOrder: number;
  isActive: boolean;
  createdAt?: string;
}

interface InnovationItem {
  _id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  technologies: string[];
  imageUrl: string;
  featured: boolean;
  displayOrder: number;
  isActive: boolean;
  createdAt?: string;
}

const AboutPage: React.FC = () => {
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [contactItems, setContactItems] = useState<ContactItem[]>([]);
  const [innovationItems, setInnovationItems] = useState<InnovationItem[]>([]);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
        
        // Fetch projects
        const projectsResponse = await fetch(`${apiUrl}/api/projects?status=active&isPublished=true`);
        if (projectsResponse.ok) {
          const data = await projectsResponse.json();
          if (data.success) {
            setProjects(data.data.projects);
          }
        }

        // Fetch team members
        const teamResponse = await fetch(`${apiUrl}/api/team`);
        if (teamResponse.ok) {
          const teamData = await teamResponse.json();
          // Sort by creation date (oldest first)
          const sortedTeam = teamData.sort((a: TeamMember, b: TeamMember) => {
            return new Date(a.createdAt || a._id).getTime() - new Date(b.createdAt || b._id).getTime();
          });
          setTeamMembers(sortedTeam);
        }

        // Fetch contact items
        const contactResponse = await fetch(`${apiUrl}/api/contacts`);
        if (contactResponse.ok) {
          const contactData = await contactResponse.json();
          if (contactData.success) {
            // Sort by display order, then by creation date
            const sortedContacts = contactData.data.sort((a: ContactItem, b: ContactItem) => {
              if (a.displayOrder !== b.displayOrder) {
                return a.displayOrder - b.displayOrder;
              }
              return new Date(a.createdAt || a._id).getTime() - new Date(b.createdAt || b._id).getTime();
            });
            setContactItems(sortedContacts);
          }
        }

        // Fetch innovation items
        const innovationResponse = await fetch(`${apiUrl}/api/innovations`);
        if (innovationResponse.ok) {
          const innovationData = await innovationResponse.json();
          if (innovationData.success) {
            // Sort by display order, then by creation date
            const sortedInnovations = innovationData.data.sort((a: InnovationItem, b: InnovationItem) => {
              if (a.displayOrder !== b.displayOrder) {
                return a.displayOrder - b.displayOrder;
              }
              return new Date(a.createdAt || a._id).getTime() - new Date(b.createdAt || b._id).getTime();
            });
            setInnovationItems(sortedInnovations);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Handle scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Layout 
      currentPage="about"
      showServicesModal={showServicesModal}
      setShowServicesModal={setShowServicesModal}
      projects={projects}
    >
      {/* Innovation Section - Full About Page */}
      <section className="max-w-7xl mx-auto px-6 py-20 innovation-section">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 text-white">
            Our Innovation
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Cutting-edge projects and research initiatives driving the future of blockchain technology
          </p>
        </div>

        {innovationItems.length > 0 ? (
          <div className="space-y-1">
            {/* First Row */}
            <div className="grid md:grid-cols-2 gap-8">
              {innovationItems.slice(0, 2).map((innovation, index) => (
                <div 
                  key={innovation._id} 
                  className="dashboard-block p-6 card-hover animate-fade-in innovation-card"
                  style={{ animationDelay: `${index * 0.3}s` }}
                >
                  {/* Innovation Image */}
                  {innovation.imageUrl && (
                    <div className="w-full h-48 mb-4 rounded-lg overflow-hidden">
                      <img
                        src={getFullImageUrl(innovation.imageUrl)}
                        alt={innovation.title}
                        className="w-full h-full object-cover"
                        onError={handleImageError}
                      />
                    </div>
                  )}

                  {/* Innovation Info */}
                  <div className="mb-3">
                    <h3 className="text-xl font-bold text-white">{innovation.title}</h3>
                  </div>
                  
                  {innovation.description && (
                    <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                      {innovation.description}
                    </p>
                  )}

                  {/* Technologies */}
                  {innovation.technologies && innovation.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {innovation.technologies.map((tech, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* DUSKPROOF Logo Between Rows */}
            <div className="flex justify-center relative py-1">
              <div className="w-20 h-20 relative z-10 logo-glow">
                <img 
                  src="/images/logo.png" 
                  alt="DUSKPROOF Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            {/* Second Row */}
            <div className="grid md:grid-cols-2 gap-8">
              {innovationItems.slice(2, 4).map((innovation, index) => (
                <div 
                  key={innovation._id} 
                  className="dashboard-block p-6 card-hover animate-fade-in innovation-card"
                  style={{ animationDelay: `${(index + 2) * 0.3 + 0.5}s` }}
                >
                  {/* Innovation Image */}
                  {innovation.imageUrl && (
                    <div className="w-full h-48 mb-4 rounded-lg overflow-hidden">
                      <img
                        src={getFullImageUrl(innovation.imageUrl)}
                        alt={innovation.title}
                        className="w-full h-full object-cover"
                        onError={handleImageError}
                      />
                    </div>
                  )}

                  {/* Innovation Info */}
                  <div className="mb-3">
                    <h3 className="text-xl font-bold text-white">{innovation.title}</h3>
                  </div>
                  
                  {innovation.description && (
                    <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                      {innovation.description}
                    </p>
                  )}

                  {/* Technologies */}
                  {innovation.technologies && innovation.technologies.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {innovation.technologies.map((tech, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-400">
            <p>No innovation projects available</p>
          </div>
        )}
      </section>

      {/* Team Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 text-white">
            Meet the Team
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Blockchain infrastructure experts dedicated to building the future of decentralized systems
          </p>
        </div>

        {teamMembers.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {teamMembers.map((member) => (
              <div key={member._id} className="dashboard-block p-6 text-center card-hover">
                {/* Member Photo */}
                <div className="w-24 h-24 mx-auto mb-4">
                  {member.imageUrl ? (
                    <img
                      src={getFullImageUrl(member.imageUrl)}
                      alt={member.name}
                      className="w-full h-full rounded-full object-cover"
                      onError={handleImageError}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-2xl text-white font-bold">
                        {member.name ? member.name.charAt(0).toUpperCase() : 'рџ‘¤'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Member Info */}
                <h3 className="text-xl font-bold text-white mb-2">{member.name || 'Team Member'}</h3>
                <p className="text-indigo-400 font-medium mb-3 uppercase tracking-wide">{member.position || 'Position'}</p>
                
                {member.description && (
                  <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                    {member.description}
                  </p>
                )}

                {/* Social Links */}
                {(member.socialLinks?.twitter || member.socialLinks?.linkedin || member.socialLinks?.github) && (
                  <div className="flex justify-center space-x-4 mt-4">
                    {member.socialLinks.twitter && (
                      <a
                        href={member.socialLinks.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-400 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.29 18.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0020 3.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.073 4.073 0 01.8 7.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 010 16.407a11.616 11.616 0 006.29 1.84" />
                        </svg>
                      </a>
                    )}
                    {member.socialLinks.linkedin && (
                      <a
                        href={member.socialLinks.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z" clipRule="evenodd" />
                        </svg>
                      </a>
                    )}
                    {member.socialLinks.github && (
                      <a
                        href={member.socialLinks.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-200 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 0C4.477 0 0 4.484 0 10.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0110 4.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.203 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.942.359.31.678.921.678 1.856 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0020 10.017C20 4.484 15.522 0 10 0z" clipRule="evenodd" />
                        </svg>
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-400">
            <p>Loading team information...</p>
          </div>
        )}
      </section>

      {/* By the Numbers Section - Dynamic from admin panel */}
      <ByTheNumbersSection />

      {/* Contact Us Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 text-white">
            Contact Us
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Get in touch with our team through your preferred communication channel
          </p>
        </div>

        {contactItems.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {contactItems.map((contact) => (
              <div key={contact._id} className="dashboard-block p-6 text-center card-hover">
                {/* Contact Info */}
                <h3 className="text-xl font-bold text-white mb-2">{contact.title || contact.type}</h3>
                
                {contact.description && (
                  <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                    {contact.description}
                  </p>
                )}

                {/* Contact Link/Info */}
                {contact.contact && (
                  <div className="mt-4">
                    {contact.type === 'Email' ? (
                      <a href={`mailto:${contact.contact}`} className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium">
                        {contact.contact}
                      </a>
                    ) : contact.type === 'Telegram' ? (
                      <a
                        href={contact.contact.startsWith('http') ? contact.contact : `https://t.me/${contact.contact.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
                      >
                        {contact.contact}
                      </a>
                    ) : contact.type === 'Twitter' ? (
                      <a
                        href={contact.contact.startsWith('http') ? contact.contact : `https://twitter.com/${contact.contact.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
                      >
                        {contact.contact}
                      </a>
                    ) : contact.type === 'LinkedIn' ? (
                      <a
                        href={contact.contact.startsWith('http') ? contact.contact : `https://linkedin.com/in/${contact.contact}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
                      >
                        {contact.contact}
                      </a>
                    ) : contact.type === 'GitHub' ? (
                      <a
                        href={contact.contact.startsWith('http') ? contact.contact : `https://github.com/${contact.contact}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
                      >
                        {contact.contact}
                      </a>
                    ) : contact.type === 'Website' || contact.contact.startsWith('http') ? (
                      <a
                        href={contact.contact}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-400 hover:text-indigo-300 transition-colors font-medium"
                      >
                        {contact.contact}
                      </a>
                    ) : (
                      <span className="text-gray-300 font-medium">{contact.contact}</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-400">
            <p>No contact information available</p>
          </div>
        )}
      </section>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-gray-800 hover:bg-gray-700 border border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white p-3 rounded-lg shadow-lg transition-all duration-300 z-50 backdrop-blur-sm"
          aria-label="Scroll to top"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}
    </Layout>
  );
};

export default AboutPage;


